import { useEffect, useRef, useState } from 'react'

import dayjs from 'dayjs'
import { sortBy } from 'lodash-es'

import { type ChartDataPoint, type ClosedDateInfo, NOW_DATE, type SelectedDate } from './useStatsPage'

// ============================================================================
// Chart Configuration Constants
// ============================================================================

// Time bucket configuration (5PM to 4AM = 11 hours = 660 minutes)
export const START_HOUR = 17 // 5PM
export const END_HOUR = 28 // 4AM next day (17 + 11 = 28)
export const BUCKET_SIZE_MINUTES = 15
export const TOTAL_CHART_MINUTES = (END_HOUR - START_HOUR) * 60 // 660 minutes

// Colors for chart lines (up to 8 lines)
export const CHART_COLORS = [
  '#3b82f6', // blue-500
  '#ef4444', // red-500
  '#22c55e', // green-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
]

// ============================================================================
// Time Bucket Utilities
// ============================================================================

export interface TimeBucket {
  time: string // "17:00", "17:15", etc.
  timeMinutes: number // Minutes from 5PM (0 = 17:00, 15 = 17:15, etc.)
}

export const generateTimeBuckets = (): TimeBucket[] => {
  const buckets: TimeBucket[] = []
  for (let hour = START_HOUR; hour < END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += BUCKET_SIZE_MINUTES) {
      const displayHour = hour >= 24 ? hour - 24 : hour
      const time = `${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      const timeMinutes = (hour - START_HOUR) * 60 + minute
      buckets.push({ time, timeMinutes })
    }
  }
  // Add final bucket at exactly 04:00 (end of chart range)
  buckets.push({ time: '04:00', timeMinutes: TOTAL_CHART_MINUTES })
  return buckets
}

/**
 * Convert a timestamp to minutes from 5PM.
 * Hours between 4AM and 5PM are clamped to chart boundaries:
 * - Before 5PM (but after 4AM) → 0 (start of chart at 17:00)
 * - After 4AM next day → max (end of chart at 04:00)
 */
export const normalizeToTimeMinutes = (timestamp: Date): number => {
  const hour = timestamp.getHours()
  const minute = timestamp.getMinutes()

  // Hours 4-16 (4AM to 4:59PM) are outside operating hours
  // Clamp them to the start of the chart (17:00 = 0 minutes)
  if (hour >= END_HOUR - 24 && hour < START_HOUR) {
    return 0
  }

  // Hours 0-3 (midnight to 3:59AM) are part of the previous evening
  // Hours 17-23 are the start of the evening
  const adjustedHour = hour < START_HOUR ? hour + 24 : hour
  const timeMinutes = (adjustedHour - START_HOUR) * 60 + minute

  // Clamp to chart range
  return Math.min(timeMinutes, TOTAL_CHART_MINUTES)
}

/**
 * Calculate current time in minutes from 5PM.
 */
export const getCurrentTimeMinutes = (now: Date): number => {
  const currentHour = now.getHours()
  const currentMinute = now.getMinutes()
  const adjustedCurrentHour = currentHour < START_HOUR ? currentHour + 24 : currentHour
  return (adjustedCurrentHour - START_HOUR) * 60 + currentMinute
}

/**
 * Check if a time (in minutes from 5PM) is within the chart range.
 */
export const isTimeInChartRange = (timeMinutes: number): boolean => {
  const maxTimeMinutes = (END_HOUR - START_HOUR) * 60
  return timeMinutes >= 0 && timeMinutes <= maxTimeMinutes
}

/**
 * Hook that returns the current time, updating every minute.
 * Optimized to only trigger re-renders when currentTimeMinutes actually changes,
 * avoiding unnecessary chart recalculations.
 */
export const useChartNow = (): Date => {
  const [now, setNow] = useState(() => new Date())
  const lastTimeMinutesRef = useRef<number>(getCurrentTimeMinutes(now))

  useEffect(() => {
    const interval = setInterval(() => {
      const newNow = new Date()
      const newTimeMinutes = getCurrentTimeMinutes(newNow)

      // Only update state if currentTimeMinutes changed
      if (newTimeMinutes !== lastTimeMinutesRef.current) {
        lastTimeMinutesRef.current = newTimeMinutes
        setNow(newNow)
      }
    }, 60_000) // Check every minute

    return () => {
      clearInterval(interval)
    }
  }, [])

  return now
}

// ============================================================================
// Closed Evening Data Utilities
// ============================================================================

/**
 * Input type for regions data.
 * Uses a minimal interface that both regionsQuery and operatingRegionsQuery satisfy.
 */
export interface RegionWithEvenings {
  _id: string
  test?: boolean | null
  evenings?: Array<{
    date: Date
    status: string
    eveningId?: number | null
  }> | null
}

/**
 * Result of building closed evening data from regions.
 * Provides both the list of closed dates and a mapping of evening IDs by date.
 */
export interface ClosedEveningData {
  /** List of closed dates with labels, sorted by most recent first */
  closedDates: ClosedDateInfo[]
  /** Map from date string (YYYY-MM-DD) to array of evening IDs for that date */
  eveningIdsByDate: Record<string, number[]>
  /** Map from date string (YYYY-MM-DD) to regionId to eveningId (for region-specific filtering) */
  eveningsByDateAndRegion: Record<string, Record<string, number>>
}

/**
 * Build evening data from regions.
 * Extracts all CLOSED and OPEN evenings up to today from non-test regions.
 * OPEN evenings are included because central admins sometimes forget to close them,
 * and we still want to be able to view their data.
 * This function is shared between useStatsPage and useMiniStats.
 *
 * @param regions - Array of regions with their evenings
 * @param locale - Locale for date formatting (e.g., 'fr-ca', 'en-ca')
 * @returns ClosedEveningData with closedDates and eveningIdsByDate
 */
export const buildClosedEveningData = (regions: RegionWithEvenings[], locale: string): ClosedEveningData => {
  const today = dayjs().format('YYYY-MM-DD')
  const dateToEveningIds: Record<string, Set<number>> = {}
  const eveningsByDateAndRegion: Record<string, Record<string, number>> = {}

  regions
    .filter((region) => !region.test || import.meta.env.DEV)
    .forEach((region) => {
      region.evenings?.forEach((evening) => {
        // Include both CLOSED and OPEN evenings (OPEN for admins who forgot to close)
        if ((evening.status === 'CLOSED' || evening.status === 'OPEN') && evening.eveningId) {
          const dateStr = evening.date.toISOString().split('T')[0]
          // Only include dates up to today
          if (dateStr <= today) {
            // Build the set of evening IDs for this date
            if (!dateToEveningIds[dateStr]) {
              dateToEveningIds[dateStr] = new Set()
            }
            dateToEveningIds[dateStr].add(evening.eveningId)

            // Build the per-region evening ID map
            if (!eveningsByDateAndRegion[dateStr]) {
              eveningsByDateAndRegion[dateStr] = {}
            }
            eveningsByDateAndRegion[dateStr][region._id] = evening.eveningId
          }
        }
      })
    })

  // Build sorted list of available dates with labels
  const closedDates: ClosedDateInfo[] = Object.keys(dateToEveningIds)
    .sort((a, b) => b.localeCompare(a)) // Most recent first
    .map((date) => ({
      date,
      label: dayjs(date).locale(locale).format('dddd D MMMM YYYY'),
    }))

  // Convert Sets to arrays
  const eveningIdsByDate: Record<string, number[]> = {}
  for (const [date, ids] of Object.entries(dateToEveningIds)) {
    eveningIdsByDate[date] = [...ids]
  }

  return { closedDates, eveningIdsByDate, eveningsByDateAndRegion }
}

// ============================================================================
// Chart Data Generation
// ============================================================================

export interface BuildChartDataOptions {
  selectedDates: SelectedDate[]
  transportRequestsByEveningId: Record<number, Array<{ submitTimestamp: Date }>>
  now: Date
}

/**
 * Build chart data points from selected dates and transport requests.
 * This is the core logic shared between useStatsPage and useMiniStats.
 */
export const buildChartData = ({
  selectedDates,
  transportRequestsByEveningId,
  now,
}: BuildChartDataOptions): ChartDataPoint[] => {
  const timeBuckets = generateTimeBuckets()

  // Calculate current time info
  const currentTimeMinutes = getCurrentTimeMinutes(now)
  const currentTimeInRange = isTimeInChartRange(currentTimeMinutes)

  // Build cumulative counts for each selected date
  const dateCounts: Record<string, Array<number | null>> = {}

  selectedDates.forEach((selectedDate) => {
    const dateKey = `date_${selectedDate.date}`
    const counts: Array<number | null> = []
    // Only truncate line at current time for "now" (live mode) and if we're in chart range
    const isLive = selectedDate.date === NOW_DATE

    // Collect all transport requests for this date
    const allRequests: Array<{ submitTimestamp: Date }> = []
    selectedDate.eveningIds.forEach((eveningId) => {
      const requests = transportRequestsByEveningId[eveningId] ?? []
      allRequests.push(...requests)
    })

    // Sort by timestamp
    const sortedRequests = sortBy(allRequests, (tr) => tr.submitTimestamp.getTime())

    // Calculate cumulative count for each time bucket
    let cumulativeCount = 0
    let requestIndex = 0

    timeBuckets.forEach((bucket) => {
      // Only truncate at current time if we're within chart hours
      if (isLive && currentTimeInRange && bucket.timeMinutes > currentTimeMinutes) {
        counts.push(null)
        return
      }

      // Count requests up to this time bucket
      while (
        requestIndex < sortedRequests.length &&
        normalizeToTimeMinutes(sortedRequests[requestIndex].submitTimestamp) <= bucket.timeMinutes
      ) {
        cumulativeCount++
        requestIndex++
      }
      counts.push(cumulativeCount)
    })

    dateCounts[dateKey] = counts
  })

  // Combine time buckets with date counts
  const points = timeBuckets.map((bucket, bucketIndex) => {
    const point: ChartDataPoint = {
      time: bucket.time,
      timeMinutes: bucket.timeMinutes,
    }
    selectedDates.forEach((selectedDate) => {
      const dateKey = `date_${selectedDate.date}`
      const value = dateCounts[dateKey]?.[bucketIndex]
      point[dateKey] = value
    })
    return point
  })

  // Add a point at the exact current time for "now" line (only if within chart hours)
  const hasNow = selectedDates.some((d) => d.date === NOW_DATE)
  let currentNowCount: number | null = null

  if (hasNow && currentTimeInRange) {
    const insertIndex = points.findIndex((p) => p.timeMinutes > currentTimeMinutes)
    const nowPoint: ChartDataPoint = {
      time: '',
      timeMinutes: currentTimeMinutes,
    }
    selectedDates.forEach((selectedDate) => {
      const dateKey = `date_${selectedDate.date}`
      if (selectedDate.date === NOW_DATE) {
        // For the "now" line, count ALL requests up to currentTimeMinutes
        // (not just up to the last bucket, which may be earlier)
        const allRequests: Array<{ submitTimestamp: Date }> = []
        selectedDate.eveningIds.forEach((eveningId) => {
          const requests = transportRequestsByEveningId[eveningId] ?? []
          allRequests.push(...requests)
        })
        const nowValue = allRequests.filter(
          (tr) => normalizeToTimeMinutes(tr.submitTimestamp) <= currentTimeMinutes
        ).length
        nowPoint[dateKey] = nowValue
        currentNowCount = nowValue
      } else {
        const prevPoint = insertIndex > 0 ? points[insertIndex - 1] : null
        const nextPoint = insertIndex < points.length ? points[insertIndex] : null
        if (prevPoint && nextPoint && prevPoint[dateKey] != null && nextPoint[dateKey] != null) {
          const ratio = (currentTimeMinutes - prevPoint.timeMinutes) / (nextPoint.timeMinutes - prevPoint.timeMinutes)
          nowPoint[dateKey] = Math.round(
            (prevPoint[dateKey] as number) + ratio * ((nextPoint[dateKey] as number) - (prevPoint[dateKey] as number))
          )
        } else {
          nowPoint[dateKey] = prevPoint?.[dateKey] ?? null
        }
      }
    })
    if (insertIndex >= 0) {
      points.splice(insertIndex, 0, nowPoint)
    } else {
      points.push(nowPoint)
    }
  }

  // Add projection line based on comparison dates (only if we have "now" and comparison dates)
  const comparisonDates = selectedDates.filter((d) => d.date !== NOW_DATE)
  if (hasNow && currentTimeInRange && comparisonDates.length > 0 && currentNowCount != null && currentNowCount > 0) {
    const nowCount: number = currentNowCount
    // Find the values of comparison dates at the current time
    const comparisonValuesAtNow: Record<string, number> = {}
    for (const compDate of comparisonDates) {
      const compKey = `date_${compDate.date}`
      // Find the point closest to currentTimeMinutes
      const nowPointInArray = points.find((p) => p.time === '')
      if (nowPointInArray?.[compKey] != null) {
        comparisonValuesAtNow[compKey] = nowPointInArray[compKey] as number
      }
    }

    // Check if we can use ratio-based projection (comparisons have data at current time)
    const hasComparisonDataAtNow = Object.values(comparisonValuesAtNow).some((v) => v > 0)

    // Calculate projection for each point from "now" onwards
    for (const point of points) {
      if (point.timeMinutes >= currentTimeMinutes) {
        let projectionValue: number | null = null

        if (hasComparisonDataAtNow) {
          // Use ratio-based projection when comparisons have data at current time
          let ratioSum = 0
          let ratioCount = 0

          for (const compDate of comparisonDates) {
            const compKey = `date_${compDate.date}`
            const compValueAtNow = comparisonValuesAtNow[compKey]
            const compValueAtBucket = point[compKey]

            if (compValueAtNow != null && compValueAtNow > 0 && compValueAtBucket != null) {
              ratioSum += (compValueAtBucket as number) / compValueAtNow
              ratioCount++
            }
          }

          if (ratioCount > 0) {
            projectionValue = Math.round(nowCount * (ratioSum / ratioCount))
          }
        } else {
          // Use offset-based projection when comparisons are still at 0
          // Project by adding the current count to the average of comparison values
          let compSum = 0
          let compCount = 0

          for (const compDate of comparisonDates) {
            const compKey = `date_${compDate.date}`
            const compValueAtBucket = point[compKey]

            if (compValueAtBucket != null) {
              compSum += compValueAtBucket as number
              compCount++
            }
          }

          if (compCount > 0) {
            const avgCompValue = compSum / compCount
            projectionValue = Math.round(nowCount + avgCompValue)
          }
        }

        if (projectionValue != null) {
          point.projection = projectionValue
        }
      }
    }
  }

  return points
}
